import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useToast } from "../../hooks/use-toast";
import { authApi } from "../../services/api";
import {
  ArrowLeft,
  Mail,
  Lock,
  Shield,
  Phone,
  CheckCircle
} from "lucide-react";

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState("login");
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    phoneCode: ""
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Step 1: validate email + password against backend
  // Backend returns 401 "Invalid admin credentials" if email/pass wrong,
  // or 401 "Invalid phone verification code" if email/pass OK but phoneCode wrong.
  // We send phoneCode="" to detect which case we're in.
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast({ title: "Missing information", description: "Please enter email and password", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.adminLogin(formData.email, formData.password, "");
      // If we get here with success=true, login is fully done (unlikely with empty code)
      if (res?.success && res?.token) {
        localStorage.setItem('admin_token', res.token);
        localStorage.setItem('admin_user', JSON.stringify(res.user));
        toast({ title: "Welcome back", description: res.user?.fullName || res.user?.email });
        navigate("/admin");
        return;
      }
      // No success → check error type (the safe wrapper returns null on 401)
      // Re-call directly to inspect status
      const probe = await fetch(
        (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com') + '/api/auth/admin/login',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email, password: formData.password, phoneCode: '' })
        }
      );
      const body = await probe.json().catch(() => ({}));
      if (probe.status === 401 && (body.detail || '').toLowerCase().includes('phone')) {
        // email/pass OK → advance to step 2
        toast({ title: "Login successful", description: "Enter your verification code" });
        setStep("verify-phone");
      } else {
        toast({
          title: "Login failed",
          description: body.detail || "Invalid email or password",
          variant: "destructive"
        });
      }
    } catch (err) {
      toast({ title: "Login error", description: err.message || "Network error", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: send full credentials with phoneCode → receive JWT
  const handlePhoneVerification = async (e) => {
    e.preventDefault();
    if (!formData.phoneCode) {
      toast({ title: "Missing code", description: "Enter the 6-digit verification code", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.adminLogin(formData.email, formData.password, formData.phoneCode);
      if (res?.success && res?.token) {
        localStorage.setItem('admin_token', res.token);
        localStorage.setItem('admin_user', JSON.stringify(res.user));
        toast({ title: "Verified", description: `Welcome ${res.user?.firstName || res.user?.email}` });
        navigate("/admin");
      } else {
        toast({ title: "Verification failed", description: "Invalid verification code", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Verification error", description: err.message || "Network error", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const sendPhoneCode = () => {
    toast({
      title: "Code valid",
      description: "Use code 123456 (phone verification is currently mock — production SMS pending)"
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-gray-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>

        <Card className="bg-white/10 backdrop-blur-lg border-gray-700">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-gradient-to-r from-emerald-500 to-blue-600 rounded-full w-fit">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-white mb-2">
              {step === "login" ? "Admin Login" : "Phone Verification"}
            </CardTitle>
            <p className="text-gray-300 text-sm">
              {step === "login"
                ? "Access the Wunli.com Print Studio admin dashboard"
                : "Enter the verification code"
              }
            </p>
          </CardHeader>

          <CardContent>
            {step === "login" ? (
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-200">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className="pl-10 bg-white/5 border-gray-600 text-white placeholder:text-gray-400"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-200">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      className="pl-10 bg-white/5 border-gray-600 text-white placeholder:text-gray-400"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white py-3"
                >
                  {loading ? "Authenticating..." : "Login to Dashboard"}
                </Button>
              </form>
            ) : (
              <form onSubmit={handlePhoneVerification} className="space-y-6">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center space-x-2 bg-blue-500/20 text-blue-200 px-4 py-2 rounded-lg">
                    <Phone className="h-4 w-4" />
                    <span className="text-sm">Code: 123456 (mock — production SMS pending)</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneCode" className="text-gray-200">Verification Code</Label>
                  <Input
                    id="phoneCode"
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={formData.phoneCode}
                    onChange={(e) => handleInputChange("phoneCode", e.target.value)}
                    className="text-center text-2xl tracking-widest bg-white/5 border-gray-600 text-white placeholder:text-gray-400"
                    maxLength={6}
                    required
                  />
                </div>

                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={sendPhoneCode}
                    className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
                  >
                    Resend Code
                  </Button>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white"
                  >
                    {loading ? "Verifying..." : "Verify"}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogin;
